import { AwaitContainer, Button, DialogRoot, DialogTrigger } from '@ibsys/kit';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/entities/profile';
import { Role, useCreateRole, useUpdateRole } from '@/entities/roles';
import { useRoles } from '@/pages/roles/RolesPage/useRoles';
import { CreateRoleDialog } from './CreateRoleDialog';
import { RolesMatrix } from './RolesMatrix';
import { PageContainer } from '@/shared/ui';

// Container component following SRP: handles data, state, and handlers only.
export const RolesPage: React.FC = () => {
  const { t } = useTranslation(['common', 'roles']);

  const { rolesQuery, getTogglePermissions, isChecked, isRoleFull, toggleFullAccess, deleteRoleByCode } = useRoles();
  const { mutateAsync: createRole, isPending: isCreating } = useCreateRole();
  const { mutateAsync: updateRole } = useUpdateRole();

  const { data } = rolesQuery;
  const roles: Role[] = data?.roles || [];

  const isEditable = useHasPermission('ADMIN_ROLE');

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleCreateRole = async (code: string, name: string) => {
    await createRole({ code, name, permissions: [] });
    setIsDialogOpen(false);
  };

  const handleRenameRole = (roleCode: string, name: string) => {
    const prevRole = roles.find((role) => role.code === roleCode);
    if (!prevRole) return;

    updateRole({ code: roleCode, body: { name, permissions: prevRole.permissions } });
  };

  return (
    <PageContainer enableScroll={false}>
      <DialogRoot open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AwaitContainer className={'h-full w-full'} isLoading={rolesQuery.isLoading}>
          <div className="grid grid-cols-1 grid-rows-[auto_1fr] h-full gap-5 w-full pb-5">
            <div className="flex w-full items-end gap-4 justify-end">
              {isEditable && (
                <DialogTrigger asChild>
                  <Button>{t('roles:createRole')}</Button>
                </DialogTrigger>
              )}
            </div>

            <RolesMatrix
              roles={roles}
              isChecked={isChecked}
              getTogglePermission={getTogglePermissions}
              isRoleFull={isRoleFull}
              onToggleFullAccess={toggleFullAccess}
              onDeleteRole={deleteRoleByCode}
              onRenameRole={handleRenameRole}
            />
          </div>

          <CreateRoleDialog isCreating={isCreating} onCreateRole={handleCreateRole} />
        </AwaitContainer>
      </DialogRoot>
    </PageContainer>
  );
};
